/**
 * Financial Modeling Prep API Provider
 * 需要API密钥，专门支持指数数据
 */

import {
  ApiProvider,
  Asset,
  AssetPrice,
  AssetType,
  TimeRange,
  HistoricalData,
  AssetSearchResult,
  AssetValidationResult,
  RequestConfig
} from '@/types/api';

import { financialModelingPrepService } from '../financialModelingPrep';
import { createApiError, categorizeHttpError } from '../errors';
import { getApiKey } from '../apiKeyManager';

export class FinancialModelingPrepProvider implements ApiProvider {
  name = 'financialmodelingprep';
  priority = 0.5; // 对于指数类型，优先于 TwelveData (1)，专门用于指数
  isHealthy = true;
  lastError?: Date;
  supportedAssetTypes: AssetType[] = ['index']; // 仅支持指数
  
  private readonly timeout = 10000;
  private availableIndexesCache: Array<{ symbol: string; name: string; exchange?: string }> | null = null;
  private cacheTimestamp: number = 0;
  private readonly cacheTTL = 24 * 60 * 60 * 1000; // 24小时缓存

  async getAssetPrice(asset: Asset, config?: RequestConfig): Promise<AssetPrice> {
    try {
      if (asset.type !== 'index') {
        throw createApiError('VALIDATION_ERROR', 'Financial Modeling Prep only supports index assets', { provider: this.name });
      }

      const apiKey = getApiKey('financialmodelingprep');
      if (!apiKey) {
        throw createApiError('AUTH_ERROR', 'Financial Modeling Prep API key is required', { provider: this.name });
      }
      
      financialModelingPrepService.setApiKey(apiKey);
      const price = await financialModelingPrepService.getIndexPrice(asset.symbol);
      
      // Ensure the returned id matches the asset.id
      return {
        ...price,
        id: asset.id,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }
  
  async getAssetPrices(assets: Asset[], config?: RequestConfig): Promise<AssetPrice[]> {
    try {
      const indexAssets = assets.filter(a => a.type === 'index');
      if (indexAssets.length === 0) {
        return [];
      }

      const apiKey = getApiKey('financialmodelingprep');
      if (!apiKey) {
        throw createApiError('AUTH_ERROR', 'Financial Modeling Prep API key is required', { provider: this.name });
      }
      
      financialModelingPrepService.setApiKey(apiKey);
      const symbols = indexAssets.map(a => a.symbol);
      const prices = await financialModelingPrepService.getBatchIndexQuotes(symbols);
      
      // Map prices back to assets to ensure correct id matching
      // Create a map of symbol -> asset for reliable matching
      const assetMap = new Map(indexAssets.map(a => [a.symbol.toUpperCase(), a]));
      
      return prices.map((price) => {
        // Match by symbol (case-insensitive) to ensure correct asset mapping
        const asset = assetMap.get(price.symbol.toUpperCase());
        if (asset) {
          return {
            ...price,
            id: asset.id, // Use asset.id instead of symbol-based id
          };
        }
        // Fallback: try to find by index if symbol matching fails
        const index = symbols.findIndex(s => s.toUpperCase() === price.symbol.toUpperCase());
        if (index >= 0 && indexAssets[index]) {
          return {
            ...price,
            id: indexAssets[index].id,
          };
        }
        return price;
      });
    } catch (error) {
      throw this.handleError(error);
    }
  }
  
  async getHistoricalData(asset: Asset, timeRange: TimeRange, config?: RequestConfig): Promise<HistoricalData> {
    try {
      if (asset.type !== 'index') {
        throw createApiError('VALIDATION_ERROR', 'Financial Modeling Prep only supports index assets', { provider: this.name });
      }

      const apiKey = getApiKey('financialmodelingprep');
      if (!apiKey) {
        throw createApiError('AUTH_ERROR', 'Financial Modeling Prep API key is required', { provider: this.name });
      }
      
      financialModelingPrepService.setApiKey(apiKey);
      const days = this.timeRangeToDays(timeRange);
      const rawData = await financialModelingPrepService.getIndexHistorical(asset.symbol, days);
      
      return {
        asset,
        timeRange,
        data: {
          prices: rawData.prices.map(([t, v]) => ({ timestamp: t, value: v })),
          volumes: rawData.total_volumes.map(([t, v]) => ({ timestamp: t, value: v })),
          marketCaps: rawData.market_caps?.map(([t, v]) => ({ timestamp: t, value: v }))
        },
        metadata: { fetchedAt: new Date(), source: this.name, interval: this.getInterval(timeRange) }
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }
  
  async searchAssets(query: string, type?: AssetType, config?: RequestConfig): Promise<AssetSearchResult[]> {
    try {
      // 只支持指数类型
      if (type && type !== 'index') {
        return [];
      }

      const apiKey = getApiKey('financialmodelingprep');
      if (!apiKey) {
        return [];
      }

      financialModelingPrepService.setApiKey(apiKey);
      
      // 获取可用指数列表（带缓存）
      const indexes = await this.getAvailableIndexes();
      
      const lowerQuery = query.toLowerCase();
      return indexes
        .filter(index => 
          index.symbol.toLowerCase().includes(lowerQuery) || 
          index.name.toLowerCase().includes(lowerQuery)
        )
        .map(index => ({
          id: index.symbol.toLowerCase().replace(/[^a-z0-9]/g, '_'),
          symbol: index.symbol.replace(/^\^/, ''), // 移除 ^ 前缀用于显示
          name: index.name,
          type: 'index' as AssetType,
          exchange: index.exchange,
        }))
        .slice(0, 50); // 限制结果数量
    } catch (error) {
      console.warn('FMP search failed:', error);
      return [];
    }
  }
  
  async validateAsset(asset: Partial<Asset>, config?: RequestConfig): Promise<AssetValidationResult> {
    if (!asset.symbol || !asset.type) {
      return { valid: false, error: 'Asset symbol and type are required' };
    }

    if (asset.type !== 'index') {
      return { valid: false, error: 'Financial Modeling Prep only supports index assets' };
    }
    
    const symbol = asset.symbol.toUpperCase();
    const testAsset: Asset = {
      id: asset.id || symbol.toLowerCase().replace(/\./g, '_'),
      symbol: symbol,
      name: asset.name || symbol,
      type: asset.type,
      priceStatus: 'loading',
      displayOrder: 0,
      isActive: true,
      ...(asset.exchange && { exchange: asset.exchange })
    };
    
    try {
      const priceData = await this.getAssetPrice(testAsset, { ...config, timeout: 5000 });
      return { valid: true, asset: { ...testAsset, price: priceData.current_price, priceStatus: 'success' as const } };
    } catch (error) {
      return { valid: false, error: `Validation failed: ${error instanceof Error ? error.message : 'Unknown error'}` };
    }
  }
  
  async testConnection(config?: RequestConfig): Promise<boolean> {
    try {
      const apiKey = getApiKey('financialmodelingprep');
      if (!apiKey) return false;
      
      financialModelingPrepService.setApiKey(apiKey);
      await financialModelingPrepService.getAvailableIndexes();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * 获取可用指数列表（带缓存）
   */
  private async getAvailableIndexes(): Promise<Array<{ symbol: string; name: string; exchange?: string }>> {
    const now = Date.now();
    
    // 如果缓存有效，直接返回
    if (this.availableIndexesCache && (now - this.cacheTimestamp) < this.cacheTTL) {
      return this.availableIndexesCache;
    }

    // 获取新数据
    try {
      const apiKey = getApiKey('financialmodelingprep');
      if (!apiKey) {
        return [];
      }

      financialModelingPrepService.setApiKey(apiKey);
      const indexes = await financialModelingPrepService.getAvailableIndexes();
      
      // 更新缓存
      this.availableIndexesCache = indexes;
      this.cacheTimestamp = now;
      
      return indexes;
    } catch (error) {
      console.warn('Failed to fetch available indexes:', error);
      // 如果获取失败，返回空数组或使用缓存
      return this.availableIndexesCache || [];
    }
  }
  
  private timeRangeToDays(timeRange: TimeRange): number {
    const map: Record<TimeRange, number> = { '1D': 1, '7D': 7, '30D': 30, '90D': 90, '1Y': 365 };
    return map[timeRange] || 7;
  }
  
  private getInterval(timeRange: TimeRange): string {
    const map: Record<TimeRange, string> = { '1D': '1h', '7D': '4h', '30D': '1d', '90D': '1d', '1Y': '1d' };
    return map[timeRange] || '1d';
  }
  
  private handleError(error: unknown): never {
    if (error instanceof Error && 'code' in error) throw error;
    if (error instanceof Error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw createApiError('NETWORK_ERROR', 'Network connection failed', { provider: this.name, originalError: error });
      }
      if (error.name === 'AbortError' || error.message.includes('timeout')) {
        throw createApiError('TIMEOUT_ERROR', 'Request timed out', { provider: this.name, originalError: error });
      }
      if ('response' in error && error.response) {
        const response = error.response as any;
        throw createApiError(categorizeHttpError(response.status, response.data?.message), response.data?.message || error.message, { provider: this.name, originalError: error });
      }
      throw createApiError('UNKNOWN_ERROR', error.message, { provider: this.name, originalError: error });
    }
    throw createApiError('UNKNOWN_ERROR', 'An unknown error occurred', { provider: this.name, originalError: error });
  }
}
